import type { ListingMapViewport } from 'app/types/listing';
import type { Region } from 'react-native-maps';

const WEB_MERCATOR_TILE_SIZE = 256;
const WEB_MERCATOR_MAX_LATITUDE = 85.05112878;
const MAX_LONGITUDE_DELTA = 360;
const MIN_MAP_ZOOM = 0;
const MAX_MAP_ZOOM = 22;
const QUERY_BUFFER_PER_SIDE = 0.75;
const QUERY_REFRESH_GUARD_PER_SIDE = 0.2;
const BOUNDS_EPSILON = 1e-9;

export interface MapQueryCoverage {
  region: Region;
  viewport: ListingMapViewport;
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.max(minimum, Math.min(maximum, value));
}

function finiteOr(value: number, fallback: number) {
  return Number.isFinite(value) ? value : fallback;
}

function normalizeLongitude(longitude: number) {
  const normalized =
    (((finiteOr(longitude, 0) + 180) % MAX_LONGITUDE_DELTA) + MAX_LONGITUDE_DELTA) % MAX_LONGITUDE_DELTA;
  return normalized - 180;
}

function normalizeDelta(delta: number, maximum: number) {
  return clamp(Math.abs(finiteOr(delta, 0)), 0, maximum);
}

function normalizeZoom(zoom: number) {
  return clamp(Math.floor(finiteOr(zoom, MIN_MAP_ZOOM)), MIN_MAP_ZOOM, MAX_MAP_ZOOM);
}

function normalizeRegion(region: Region): Region {
  return {
    latitude: clamp(finiteOr(region.latitude, 0), -WEB_MERCATOR_MAX_LATITUDE, WEB_MERCATOR_MAX_LATITUDE),
    longitude: normalizeLongitude(region.longitude),
    latitudeDelta: normalizeDelta(region.latitudeDelta, WEB_MERCATOR_MAX_LATITUDE * 2),
    longitudeDelta: normalizeDelta(region.longitudeDelta, MAX_LONGITUDE_DELTA),
  };
}

function getMapZoom(longitudeDelta: number, viewportWidth: number) {
  if (longitudeDelta <= 0) return MAX_MAP_ZOOM;

  const width = Number.isFinite(viewportWidth) && viewportWidth > 0 ? viewportWidth : WEB_MERCATOR_TILE_SIZE;
  const zoom = Math.log2((MAX_LONGITUDE_DELTA * width) / (WEB_MERCATOR_TILE_SIZE * longitudeDelta));
  return normalizeZoom(zoom);
}

function getLongitudeBounds(longitude: number, longitudeDelta: number) {
  if (longitudeDelta >= MAX_LONGITUDE_DELTA) return { east: 180, west: -180 };

  return {
    east: normalizeLongitude(longitude + longitudeDelta / 2),
    west: normalizeLongitude(longitude - longitudeDelta / 2),
  };
}

function scaleRegion(region: Region, scale: number): Region {
  const normalizedRegion = normalizeRegion(region);
  return {
    ...normalizedRegion,
    latitudeDelta: normalizeDelta(normalizedRegion.latitudeDelta * scale, WEB_MERCATOR_MAX_LATITUDE * 2),
    longitudeDelta: normalizeDelta(normalizedRegion.longitudeDelta * scale, MAX_LONGITUDE_DELTA),
  };
}

function getLongitudeSpan(viewport: ListingMapViewport) {
  if (viewport.west === -180 && viewport.east === 180) return MAX_LONGITUDE_DELTA;
  return (((viewport.east - viewport.west) % MAX_LONGITUDE_DELTA) + MAX_LONGITUDE_DELTA) % MAX_LONGITUDE_DELTA;
}

function getEastwardLongitudeDistance(from: number, to: number) {
  return (((to - from) % MAX_LONGITUDE_DELTA) + MAX_LONGITUDE_DELTA) % MAX_LONGITUDE_DELTA;
}

function containsViewport(outer: ListingMapViewport, inner: ListingMapViewport) {
  if (inner.north > outer.north + BOUNDS_EPSILON || inner.south < outer.south - BOUNDS_EPSILON) return false;

  const outerSpan = getLongitudeSpan(outer);
  const innerSpan = getLongitudeSpan(inner);
  if (outerSpan >= MAX_LONGITUDE_DELTA - BOUNDS_EPSILON) return true;
  if (innerSpan > outerSpan + BOUNDS_EPSILON) return false;

  const innerStart = getEastwardLongitudeDistance(outer.west, inner.west);
  return innerStart + innerSpan <= outerSpan + BOUNDS_EPSILON;
}

export function regionToMapViewport(region: Region, viewportWidth: number, zoomOverride?: number): ListingMapViewport {
  const normalizedRegion = normalizeRegion(region);
  const { east, west } = getLongitudeBounds(normalizedRegion.longitude, normalizedRegion.longitudeDelta);

  return {
    north: clamp(
      normalizedRegion.latitude + normalizedRegion.latitudeDelta / 2,
      -WEB_MERCATOR_MAX_LATITUDE,
      WEB_MERCATOR_MAX_LATITUDE,
    ),
    south: clamp(
      normalizedRegion.latitude - normalizedRegion.latitudeDelta / 2,
      -WEB_MERCATOR_MAX_LATITUDE,
      WEB_MERCATOR_MAX_LATITUDE,
    ),
    east,
    west,
    zoom:
      zoomOverride === undefined
        ? getMapZoom(normalizedRegion.longitudeDelta, viewportWidth)
        : normalizeZoom(zoomOverride),
  };
}

export function createMapQueryCoverage(region: Region, viewportWidth: number): MapQueryCoverage {
  const displayViewport = regionToMapViewport(region, viewportWidth);
  const coverageRegion = scaleRegion(region, 1 + QUERY_BUFFER_PER_SIDE * 2);

  return {
    region: coverageRegion,
    viewport: regionToMapViewport(coverageRegion, viewportWidth, displayViewport.zoom),
  };
}

export function shouldRefreshMapQueryCoverage(coverage: MapQueryCoverage, region: Region, viewportWidth: number) {
  const displayViewport = regionToMapViewport(region, viewportWidth);
  if (displayViewport.zoom !== coverage.viewport.zoom) return true;

  const guardedRegion = scaleRegion(region, 1 + QUERY_REFRESH_GUARD_PER_SIDE * 2);
  const guardedViewport = regionToMapViewport(guardedRegion, viewportWidth, displayViewport.zoom);
  return !containsViewport(coverage.viewport, guardedViewport);
}

export function isCoordinateInMapViewport(
  coordinate: { latitude: number; longitude: number },
  viewport: ListingMapViewport,
) {
  if (!Number.isFinite(coordinate.latitude) || !Number.isFinite(coordinate.longitude)) return false;
  if (coordinate.latitude < viewport.south || coordinate.latitude > viewport.north) return false;

  const longitudeSpan = getLongitudeSpan(viewport);
  if (longitudeSpan >= MAX_LONGITUDE_DELTA - BOUNDS_EPSILON) return true;

  const longitude = normalizeLongitude(coordinate.longitude);
  return getEastwardLongitudeDistance(viewport.west, longitude) <= longitudeSpan + BOUNDS_EPSILON;
}
